import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import Button from 'apollo-react/components/Button';
import Typography from '../StyledTypography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import TextInput from '../Form/textInput';
import styles from './CommentForm.module.css';
import { FormikDocumentUpload } from '../FormikComponent';
import { toast } from 'react-toastify';

const CommentForm = ({
  subFormId,
  fieldId,
  onAddComment,
  onClose,
  onCommentAdded,
  hideCancelButton = false,
  hideTitleSeparator = false,
  compact = false,
  hideAttachment = false,
}) => {
  const { t } = useTranslation();

  const initialValues = {
    comment: '',
    attachedFiles: [],
  };

  const handleAddComment = async (values, { resetForm, setSubmitting }) => {
    if (values.comment.trim()) {
      try {
        const attachedFiles =
          values.attachedFiles && values.attachedFiles.length > 0
            ? values.attachedFiles
            : null;

        const newComment = await onAddComment({
          comment: values.comment,
          subFormId,
          fieldId,
          attachedFiles,
        });
        resetForm();
        if (onCommentAdded && newComment) {
          onCommentAdded(newComment);
        }
        if (onClose) onClose();
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        setSubmitting(false);
      }
    } else {
      toast.error(t('enter_comment'), { position: 'top-center' });
    }
  };

  return (
    <Box className={`${styles.container} ${compact ? styles.compact : ''}`}>
      {!compact && (
        <Typography
          variant="title1"
          component="h2"
          className={`${styles.header} ${hideTitleSeparator ? styles.headerNoBorder : styles.headerWithBorder}`}
        >
          {t('add_comment')}
        </Typography>
      )}

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={handleAddComment}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Grid container spacing={compact ? 1 : 2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <TextInput
                  label={compact ? '' : t('comment')}
                  placeholder={t('enter_comment_placeholder')}
                  multiline={true}
                  minRows={compact ? 1 : 2}
                  textareaHeight="auto"
                  showInfoIcon={false}
                  fullWidth={true}
                  value={values.comment}
                  onChange={(val) => setFieldValue('comment', val)}
                />
              </Grid>

              {!hideAttachment && (
                <Grid size={12}>
                  <FormikDocumentUpload
                    name="attachedFiles"
                    label={t('attach_file_optional')}
                    helperText={t('upload_file_supported_formats')}
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z"
                    required={false}
                    maxFiles={1}
                    multiple={false}
                  />
                </Grid>
              )}
            </Grid>

            <Box className={styles.buttonContainer}>
              {!hideCancelButton && onClose && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  className={styles.button}
                  size={compact ? 'small' : 'medium'}
                >
                  {t('cancel')}
                </Button>
              )}

              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={styles.button}
                size={compact ? 'small' : 'medium'}
              >
                {t('add_comment')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CommentForm;
